/**
 * Zoriae — Complete-the-Look quick-add engine (ported from the TexTale
 * portable component spec §6; CONFIG swapped to Horizon's cart pipeline).
 *
 * Integration contract (Horizon 4.x):
 *  - dispatch CartLinesUpdateEvent(action:'add') with a deferred promise
 *  - POST Theme.routes.cart_add_url with FormData(id, quantity, sections)
 *  - resolve the deferred with { cart, detail:{ sections, items, didError } }
 *    → cart-items-component morphs the drawer, cart-drawer-component
 *    auto-opens (auto_open_cart_drawer setting).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__zctlQuickAddInit) return;
  window.__zctlQuickAddInit = true;

  /* ── helpers ─────────────────────────────────────────────── */
  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function sized(u, w) {
    if (!u) return u;
    if (u.indexOf('//') === 0) u = 'https:' + u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 720);
  }
  function money(cents) {
    try {
      const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      });
      return fmt.format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* 色板名 → 颜色（数据无 swatch metafield 时的兜底渲染） */
  const COLOR_MAP = {
    black: '#141414', white: '#f7f7f7', clear: '#e9e9e9', transparent: '#e9e9e9', pink: '#f2b8c6',
    brown: '#8b5e3c', purple: '#9b7fd4', gray: '#9a9a9a', grey: '#9a9a9a', gold: '#d4af37',
    green: '#3f6b4f', yellow: '#e8c837', blue: '#3a6ea5', navy: '#22304d', red: '#c0392b',
    silver: '#c8c8cc', orange: '#e67e22', beige: '#e6d8c3', champagne: '#f0d9b5', leopard: '#c49a6c',
    tea: '#c9a86a', mint: '#a8d8c8', lavender: '#c4b0e0', ginger: '#d99a4e', sapphire: '#2456a6',
    jade: '#6aa88a', camo: '#5c6b4a', neon: '#b6f04a', speckled: '#f0efe8',
    multicolor: 'linear-gradient(90deg,#e74c3c,#f1c40f,#2ecc71,#3498db,#9b59b6)',
  };
  function colorCss(name) {
    const n = String(name).toLowerCase().trim();
    if (n.indexOf('/') > -1) {
      const p = n.split('/').map((s) => colorCss(s.trim()) || '#bbb');
      return 'linear-gradient(90deg,' + p[0] + ' 50%,' + p[1] + ' 50%)';
    }
    for (const k in COLOR_MAP) if (n.indexOf(k) > -1) return COLOR_MAP[k];
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
    return 'hsl(' + (h % 360) + ',28%,74%)';
  }
  function paintSwatch(el, name) {
    el.style.background = colorCss(name);
  }
  function paintDots(scope) {
    (scope || document).querySelectorAll('[data-zcolor]').forEach((d) => paintSwatch(d, d.dataset.zcolor));
  }

  /* ── CONFIG（spec §3c 四个平台接触点） ────────────────────── */
  const CONFIG = {
    fetchProduct: (h) => fetch('/products/' + encodeURIComponent(h) + '.js').then((r) => {
      if (!r.ok) throw new Error('product fetch failed');
      return r.json();
    }),
    fetchRecommendationsHTML: (sectionId, productId, limit) => {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return fetch(
        root + 'recommendations/products?section_id=' + encodeURIComponent(sectionId)
          + '&product_id=' + encodeURIComponent(productId) + '&limit=' + encodeURIComponent(limit) + '&intent=related'
      ).then((r) => (r.ok ? r.text() : ''));
    },
    addToCart: (variantId, qty, sourceEl) => {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', qty);
      const ids = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (ids.length) fd.append('sections', ids.join(','));

      const deferred = CartLinesUpdateEvent.createPromise();
      (sourceEl || document.body).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'zoriae-quick-add',
          lines: [{ merchandiseId: variantId, quantity: qty }],
          promise: deferred.promise,
        })
      );

      const url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
      return fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      })
        .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
        .then(({ ok, d }) => {
          if (!ok || d.status) {
            const err = new Error(d.description || d.message || 'Add to cart failed');
            deferred.reject(err);
            throw err;
          }
          const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
          return fetch(cartUrl + '.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then((r) => r.json())
            .then((ajaxCart) => {
              deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  sections: d.sections,
                  source: 'zoriae-quick-add',
                  itemCount: qty,
                  didError: false,
                },
              });
              return d;
            })
            .catch(() => {
              deferred.resolve({ cart: null, detail: { sections: d.sections, source: 'zoriae-quick-add', didError: false } });
              return d;
            });
        });
    },
    onAdded: () => {
      // auto_open_cart_drawer=true 时 cart-drawer-component 自己会开；这里兜底
      setTimeout(() => {
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && !drawer.hasAttribute('open') && typeof drawer.open === 'function') drawer.open();
      }, 250);
    },
    formatMoney: money,
    colorRegex: /colou?r/i,
  };

  /* ── 推荐位自举：hidden 的 [data-zctl] 自己拉卡片 ─────────── */
  function bootRecommendations() {
    document.querySelectorAll('.complete-the-look[data-zctl][hidden]').forEach((sec) => {
      const pid = sec.getAttribute('data-zctl-product-id');
      const sid = sec.getAttribute('data-zctl-section-id');
      const lim = sec.getAttribute('data-zctl-limit') || '6';
      if (!pid || !sid || sec.dataset.zctlLoading) return;
      sec.dataset.zctlLoading = '1';
      CONFIG.fetchRecommendationsHTML(sid, pid, lim)
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll('.complete-the-look__item');
          if (!items.length) return;
          const scroller = sec.querySelector('.complete-the-look__scroller');
          items.forEach((it) => scroller.appendChild(document.importNode(it, true)));
          sec.hidden = false;
          relocateAll(sec);
          paintDots(sec);
        })
        .catch(() => {});
    });
  }

  /* ── 把每个 + 移进卡片的 .image-cont（spec §6） ───────────── */
  function relocateAll(scope) {
    (scope || document).querySelectorAll('.ctl-add').forEach((btn) => {
      if (btn.dataset.ctlLocated) return;
      const item = btn.closest('.complete-the-look__item, li, .product-card, product-card') || btn.parentElement;
      const ic = item && item.querySelector('.image-cont');
      if (ic) {
        ic.appendChild(btn);
        btn.dataset.ctlLocated = '1';
      }
    });
  }

  const sheet = document.querySelector('.ctl-quickadd');
  if (!sheet) return;
  const body = sheet.querySelector('.ctl-quickadd__body');
  const cache = {};
  let current = null;

  relocateAll(document);
  paintDots(document);
  bootRecommendations();
  if (window.MutationObserver) {
    let pend = false;
    const onMut = () => {
      pend = false;
      relocateAll(document);
      paintDots(document);
      bootRecommendations(); // section morph 后重建的 hidden shell 重新拉卡
    };
    new MutationObserver(() => {
      if (pend) return;
      pend = true;
      requestAnimationFrame(onMut);
      setTimeout(onMut, 80); // rAF 节流兜底
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── open / close（带 320ms 竞态守卫；滚动锁只随 sheet） ──── */
  let hideTimer = null;
  function lockScroll(on) {
    document.documentElement.style.overflow = on ? 'hidden' : '';
  }
  function open() {
    clearTimeout(hideTimer);
    sheet.hidden = false;
    const show = () => sheet.classList.add('is-open');
    requestAnimationFrame(show);
    setTimeout(show, 60); // rAF 兜底（后台标签/节流环境）
    lockScroll(true);
  }
  function close() {
    sheet.classList.remove('is-open');
    hideTimer = setTimeout(() => {
      if (!sheet.classList.contains('is-open')) {
        sheet.hidden = true;
        body.innerHTML = '';
      }
      lockScroll(false);
    }, 320);
  }
  sheet.addEventListener('click', (e) => {
    if (e.target.closest('[data-ctl-close]')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !sheet.hidden) close();
  });

  /* ── variant / option helpers（spec §6 原样） ─────────────── */
  function optionGroups(p) {
    return (p.options || []).map((o, i) => {
      const name = typeof o === 'string' ? o : o.name || 'Option ' + (i + 1);
      const set = [];
      p.variants.forEach((v) => {
        const val = v.options[i];
        if (val && set.indexOf(val) === -1) set.push(val);
      });
      return { name, position: i, values: set };
    });
  }
  function findVariant(p, sel) {
    return p.variants.find((v) => v.options.every((val, i) => sel[i] === val));
  }
  function valueAvailable(p, pos, value, sel) {
    return p.variants.some((v) => {
      if (v.options[pos] !== value) return false;
      return (
        Object.keys(sel).every((k) => {
          k = +k;
          if (k === pos) return true;
          return sel[k] == null || v.options[k] === sel[k];
        }) && v.available
      );
    });
  }
  function buildColorImages(p, colorPos) {
    if (colorPos < 0 || !p.media || !p.media.length) return null;
    const marks = [];
    const seen = {};
    p.variants.forEach((v) => {
      const c = v.options[colorPos];
      const pos = v.featured_media && v.featured_media.position;
      if (c != null && pos && !seen[c]) {
        seen[c] = 1;
        marks.push({ color: c, pos });
      }
    });
    if (!marks.length) return null;
    marks.sort((a, b) => a.pos - b.pos);
    const groups = {};
    p.media.forEach((m) => {
      if (m.media_type && m.media_type !== 'image') return;
      let cur = marks[0].color;
      for (let i = 0; i < marks.length; i++) {
        if (m.position >= marks[i].pos) cur = marks[i].color;
        else break;
      }
      (groups[cur] = groups[cur] || []).push(m.src);
    });
    return groups;
  }
  function galleryFor(p, color) {
    const srcs =
      current.colorImages && color != null && current.colorImages[color] && current.colorImages[color].length
        ? current.colorImages[color]
        : p.images && p.images.length
          ? p.images
          : p.featured_image
            ? [p.featured_image]
            : [];
    return srcs.map((src) => '<div class="ctl-qa__slide"><img src="' + sized(src) + '" alt="" loading="lazy"></div>').join('');
  }
  function setGallery(color) {
    const g = body.querySelector('.ctl-qa__gallery');
    if (g) {
      g.innerHTML = galleryFor(current.product, color);
      g.scrollTop = 0;
      g.scrollLeft = 0;
    }
  }

  /* ── render / refresh / bind（spec §6 原样 + esc 转义） ───── */
  function render(p) {
    current = { product: p, selected: {} };
    const single = p.variants.length === 1 && (p.variants[0].title === 'Default Title' || (p.options || []).length === 0);
    const ogs = optionGroups(p);
    const colorPos = single ? -1 : ogs.findIndex((o) => CONFIG.colorRegex.test(o.name));
    current.colorPos = colorPos;
    current.colorImages = buildColorImages(p, colorPos);
    const initColor = current.colorImages && colorPos > -1 && ogs[colorPos] ? ogs[colorPos].values[0] : null;
    if (initColor != null) current.selected[colorPos] = initColor;

    let h = '<div class="ctl-qa__gallery">' + galleryFor(p, initColor) + '</div><div class="ctl-qa__main">';
    h +=
      '<div class="ctl-qa__head"><p class="ctl-qa__title">' + esc(p.title)
      + '</p><div class="ctl-qa__priceline"><span class="ctl-qa__price" data-ctl-price>' + CONFIG.formatMoney(p.price)
      + '</span><a class="ctl-qa__details" href="/products/' + esc(p.handle) + '">View product details</a></div></div>';
    if (!single)
      ogs.forEach((opt) => {
        const isColor = CONFIG.colorRegex.test(opt.name);
        h += '<div class="ctl-qa__opt"><p class="ctl-qa__opt-name">' + esc(opt.name) + '</p><div class="ctl-qa__values' + (isColor ? ' ctl-qa__values--swatch' : '') + '">';
        opt.values.forEach((val) => {
          const v = String(val);
          const ev = esc(v);
          const pressed = current.selected[opt.position] === val ? 'true' : 'false';
          h += isColor
            ? '<button type="button" class="ctl-qa__val ctl-qa__swatch" data-pos="' + opt.position + '" data-val="' + ev + '" data-swatch="' + esc(v.toLowerCase()) + '" title="' + ev + '" aria-pressed="' + pressed + '">' + ev + '</button>'
            : '<button type="button" class="ctl-qa__val" data-pos="' + opt.position + '" data-val="' + ev + '" aria-pressed="' + pressed + '">' + ev + '</button>';
        });
        h += '</div></div>';
      });
    h += '<div class="ctl-qa__addrow"><button type="button" class="ctl-qa__add"' + (single ? '' : ' disabled') + '>' + (single ? 'Add to bag' : 'Select options') + '</button></div></div>';
    body.innerHTML = h;
    body.querySelectorAll('.ctl-qa__swatch').forEach((s) => paintSwatch(s, s.dataset.swatch));
    if (single) current.variant = p.variants[0];
    bind();
    if (initColor != null) refresh();
  }
  function refresh() {
    const p = current.product;
    body.querySelectorAll('.ctl-qa__val').forEach((b) => {
      b.dataset.soldout = valueAvailable(p, +b.dataset.pos, b.dataset.val, current.selected) ? 'false' : 'true';
    });
    const all = (p.options || []).every((o, i) => current.selected[i] != null);
    const add = body.querySelector('.ctl-qa__add');
    if (all) {
      const v = findVariant(p, current.selected);
      current.variant = v;
      if (v && v.available) {
        add.disabled = false;
        add.textContent = 'Add to bag';
        body.querySelector('[data-ctl-price]').textContent = CONFIG.formatMoney(v.price);
      } else {
        add.disabled = true;
        add.textContent = 'Sold out';
      }
    } else {
      add.disabled = true;
      add.textContent = 'Select options';
    }
  }
  function bind() {
    body.querySelectorAll('.ctl-qa__val').forEach((b) => {
      b.addEventListener('click', () => {
        if (b.dataset.soldout === 'true') return;
        const pos = +b.dataset.pos;
        current.selected[pos] = b.dataset.val;
        body.querySelectorAll('.ctl-qa__val[data-pos="' + pos + '"]').forEach((x) => {
          x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
        });
        if (pos === current.colorPos) setGallery(b.dataset.val);
        refresh();
      });
    });
    const add = body.querySelector('.ctl-qa__add');
    add.addEventListener('click', () => {
      if (add.disabled || !current.variant) return;
      add.disabled = true;
      add.textContent = 'Adding…';
      CONFIG.addToCart(current.variant.id, 1, add)
        .then(() => {
          close();
          CONFIG.onAdded();
        })
        .catch(() => {
          add.disabled = false;
          add.textContent = 'Try again';
        });
    });
  }

  /* ── 事件委托：任何 + 都能开 sheet（AJAX 注入的卡片也行） ── */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('.ctl-add');
    if (!a || a.disabled) return;
    e.preventDefault();
    const handle = a.dataset.ctlHandle;
    // 购物车抽屉是 top-layer <dialog>，会盖住 sheet：先收抽屉再开 sheet（加购后会自动重新打开）
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
    open();
    body.innerHTML = '<p style="padding:34px;text-align:center;color:#5c5c5c">Loading…</p>';
    if (cache[handle]) return render(cache[handle]);
    CONFIG.fetchProduct(handle)
      .then((p) => {
        cache[handle] = p;
        render(p);
      })
      .catch(() => {
        body.innerHTML = '<p style="padding:34px;text-align:center"><a href="/products/' + esc(handle) + '">View product</a></p>';
      });
  });
})();
